<!--

	Printable page with scope and sequence content for a subject

-->
<template>
	<div class="mia-ScopeAndSequence">
		<a class="btn mia-btn-primary mia-downloadButton" @click.prevent="generatePDF">
			<promise-loader :promise="scopeAndSequenceDownloadingPromise">
				<inline-svg class="mia-Icon" src="@theme/icons/download_icon.svg" />
			</promise-loader>
			<span v-tr="'scopeandsequence_print'" />
		</a>
		<vue3-html2pdf
			ref="html2Pdf"
			pdf-format="a4"
			pdf-content-width="auto"
			:enable-download="false"
			:manual-pagination="true"
			:html-to-pdf-options="{
				margin: [0.2, 0.45, 0.45, 0.45],
				filename: filename,
				pagebreak: { avoid: ['.mia-lesson', '.mia-unitName'] },
				jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
				html2canvas: { scrollX: 0, scrollY: 0, scale: 1.5 }
			}"
			@beforeDownload="beforeDownload($event)"
		>
			<template #pdf-content>
				<scope-and-sequence-content :pdf="true" :config="config" />
			</template>
		</vue3-html2pdf>
		<scope-and-sequence-content :pdf="false" :config="config" />
	</div>
</template>
<script lang="ts">
import { MiaPlaza } from "@/Reinforced.Typings";
import { Component, Prop, toNative, Vue, Watch } from "vue-facing-decorator";
import ScopeAndSequenceContent from "./ScopeAndSequenceContent.vue";
import ResponsiveButton from "../../../../Control/Vue/ResponsiveButton.vue";
import I18Next from "@/Control/Vue/I18Next";
import { DefaultText } from "@/Control/Vue/Defaults";
import PromiseLoader from "@/common/PromiseLoader.vue";
import InlineSvg from "vue-inline-svg";
import { Vue3Html2pdf } from "@/chunks/Pdf";

@Component({
	components: {
		ScopeAndSequenceContent,
		ResponsiveButton,
		PromiseLoader,
		InlineSvg,
		Vue3Html2pdf,
	},
})
export class ScopeAndSequence extends Vue {
	@Prop({required: true}) config!: MiaPlaza.LO.Curriculum.ScopeAndSequence.IScopeAndSequenceData;

	scopeAndSequenceDownloadingPromise: Promise<any> | null = null;
	scopeAndSequenceFilename = null as null | string;
	
	generatePDF() {
		(this.$refs.html2Pdf as any).generatePdf();
	}

	// When creating a PDF, we want to avoid the canvas size limitation problem,
	// so we generate each element that contains the `mia-pageBreak--before` class as
	// a new canvas to add to the PDF file. Here we generate each course, the first page
	// and the last two pages as separate canvases
	beforeDownload({ html2pdf, options, pdfContent }: any) {
		const content = pdfContent.firstElementChild.children;
		const pages: any = [];
		const styleClasses = ["mia-ScopeAndSequenceContent", "mia-pdf"]

		let container = document.createElement("div");
		container.classList.add(...styleClasses);

		for (let i = 0; i < content.length; i++) {
			if (content[i].classList.contains("mia-pageBreak--before")) {
				pages.push(container);

				container = document.createElement("div");
				container.classList.add(...styleClasses);
			}
			container.appendChild(content[i].cloneNode(true));
		}

		if (container.children.length > 0) {
			pages.push(container);
		}
		
		this.scopeAndSequenceDownloadingPromise = new Promise((resolve) => {
			let worker = html2pdf().set(options).from(pages[0]).toPdf();
			
			pages.slice(1).forEach((page: any) => {
				worker = worker.get('pdf').then((pdf: any) => {
					pdf.addPage();
				}).from(page).toContainer().toCanvas().toPdf();
			});
			resolve(worker.save());
		});
	}

	get filename() {
		if (this.scopeAndSequenceFilename === null) {
			return DefaultText;
		}

		return this.scopeAndSequenceFilename;
	}

	@Watch("config", { immediate: true })
	protected async onConfigChange() {
		this.scopeAndSequenceFilename = null;
		this.scopeAndSequenceFilename = (await I18Next.tr("scopeandsequence_filename", {
			// We do not use js-joda here since we do not want to pull js-joda as a dependency into the landing pages which use this component.
			"date": new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' }),
			"subject": this.config.Subject
		}))
	}
}
export default toNative(ScopeAndSequence);

</script>
<style lang="scss">
.mia-ScopeAndSequence {
	position: relative;

	[disabled] {
		.mia-ScopeAndSequenceDownloadButtonIcon {
			display: none;
		}
	}
	
	.mia-downloadButton {
		position: absolute;
		right: 0;
		top: 0;
		z-index: theme.$mia-zIndex-1--foreground;
	}
}
</style>
